/**
 * Reglas de presentación de los renglones de una bitácora masiva (timbrado o complementos).
 *
 * Las comparten el detalle agrupado del navbar (`bulk-registros`) y la lista filtrable del
 * drawer del histórico de Cargas, que desde el 2026-10-09 usan el MISMO componente
 * clasifiquen un renglón igual.
 */
import {
  severidadRegistro,
  type SeveridadRegistro,
} from '@core/complementos-bulk-tracking.service';
import type { RegistroBitacora } from '@shared/models/bitacora-masiva.model';

/** Qué renglones muestra la lista: todos, o los de una severidad. */
export type FiltroRegistros = 'todos' | SeveridadRegistro;

/**
 * Orden de la lista: el error primero porque es lo único que pide una acción del usuario, y
 * lo que sigue en proceso antes que lo terminado bien.
 */
const PRIORIDAD_SEVERIDAD: Readonly<Record<SeveridadRegistro, number>> = {
  error: 0,
  proceso: 1,
  exito: 2,
};

/**
 * Renglones de la severidad pedida, con los errores primero. El orden es estable: dentro de
 * una severidad se conserva el de encolado, que es el del archivo de carga.
 *
 * @param registros Renglones cargados de la bitácora.
 * @param filtro Severidad a mostrar, o `'todos'`.
 * @returns Una lista nueva; la de entrada no se modifica.
 */
export function filtrarRegistros(
  registros: readonly RegistroBitacora[],
  filtro: FiltroRegistros,
): RegistroBitacora[] {
  return registros
    .filter((registro) => filtro === 'todos' || severidadRegistro(registro) === filtro)
    .sort(
      (a, b) =>
        PRIORIDAD_SEVERIDAD[severidadRegistro(a)] - PRIORIDAD_SEVERIDAD[severidadRegistro(b)],
    );
}

/**
 * Ancla del renglón (p. ej. `"Fila 12"`): la referencia que el usuario capturó, así que es
 * por donde busca.
 *
 * `referenciaCliente` es opcional en el contrato. En el timbrado el siguiente mejor ancla es
 * el pedido que se timbró; si no, el id de bitácora, que sigue siendo un identificador corto
 * y estable.
 *
 * @param registro Renglón de bitácora.
 * @returns Referencia corta del renglón.
 */
export function referenciaCorta(registro: RegistroBitacora): string {
  const referencia = registro.referenciaCliente?.trim();
  if (referencia) return referencia;
  if (registro.idOrder) return `Pedido ${registro.idOrder}`;
  return registro.id != null ? `#${registro.id}` : 'Sin referencia';
}

/**
 * Estatus legible del renglón. Distingue lo que el color no puede: dentro de los errores,
 * "Error de validación" y "Error del PAC" son el mismo rojo.
 *
 * @param registro Renglón de bitácora.
 * @returns Descripción del estatus, o una según la severidad.
 */
export function estatusTexto(registro: RegistroBitacora): string {
  const descripcion = registro.descripcionEstatus?.trim();
  if (descripcion) return descripcion;

  const severidad = severidadRegistro(registro);
  if (severidad === 'error') return 'Error';
  return severidad === 'exito' ? 'Timbrado' : 'En proceso';
}

/**
 * Escribe en el portapapeles.
 *
 * @param texto Texto a copiar.
 * @returns `false` cuando el contexto no da permiso, para avisarlo en vez de fallar en
 *   silencio.
 */
export async function escribirPortapapeles(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}
